import { type FC, useState } from 'react';
import { Button, Col, Modal, Row, Skeleton } from 'antd';
import { TbLifebuoy, TbMessage2, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EXTERNAL_LINKS, NOTION_FORMS } from '@/shared/constants/externalLinks';
import type { NotionFormKind } from '@/shared/constants/externalLinks';

import { closeNotionFormModal, useNotionFormModalStore } from './store';

interface NotionFormFrameProps {
    kind: NotionFormKind;
}

const NotionFormFrame: FC<NotionFormFrameProps> = ({ kind }) => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(true);
    const form = NOTION_FORMS[kind];

    return (
        <div
            style={{
                position: 'relative',
                height: 600,
                overflow: 'hidden',
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                backgroundColor: token.colorBgBase,
            }}
        >
            {loading && (
                <div
                    aria-hidden
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        padding: token.padding,
                        backgroundColor: token.colorBgBase,
                    }}
                >
                    <Skeleton active title paragraph={{ rows: 12 }} />
                </div>
            )}
            <iframe
                src={form.href}
                title={form.iframeTitle}
                width='100%'
                height='600'
                frameBorder='0'
                allowFullScreen
                onLoad={() => setLoading(false)}
                style={{ display: 'block', border: 0, borderRadius: token.borderRadius }}
            />
        </div>
    );
};

const NotionFormModal: FC = () => {
    const { token } = useAntDesignTheme();
    const { open, kind, title } = useNotionFormModalStore();
    const form = NOTION_FORMS[kind];
    const FormIcon = kind === 'feedback' ? TbMessage2 : TbLifebuoy;

    return (
        <Modal
            destroyOnHidden
            footer={
                <Row align='middle' wrap={false} gutter={token.marginSM}>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <LearnMoreFooter
                            align='left'
                            href={EXTERNAL_LINKS.documentation.home}
                            message={<>Learn more about getting help in</>}
                            linkText='our docs'
                        />
                    </Col>
                    <Col flex='none'>
                        <Button onClick={closeNotionFormModal}>Close</Button>
                    </Col>
                </Row>
            }
            open={open}
            width='min(56rem, calc(100vw - 2rem))'
            onCancel={closeNotionFormModal}
            closeIcon={<TbX size='1rem' style={{ display: 'block' }} />}
            title={
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: token.marginXS,
                        lineHeight: token.lineHeight,
                    }}
                >
                    <FormIcon
                        size='1rem'
                        color={token.colorTextTertiary}
                        style={{ display: 'block', flex: '0 0 auto' }}
                    />
                    <span>{title || form.title}</span>
                </span>
            }
        >
            <NotionFormFrame key={`${kind}-${open ? 'open' : 'closed'}`} kind={kind} />
        </Modal>
    );
};

export default NotionFormModal;
